import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

/**
 * Invoice PDF Generator
 *
 * Builds a clean, professional invoice PDF programmatically with pdf-lib.
 * Supports multi-page layout when line items overflow.
 */

// Page dimensions (US Letter)
const PAGE_W = 612;
const PAGE_H = 792;
const MARGIN = 50;
const CONTENT_W = PAGE_W - MARGIN * 2;

// Table column positions (x offsets from left margin)
const COL = {
  desc: 0,
  qty: CONTENT_W - 190,
  price: CONTENT_W - 120,
  amount: CONTENT_W - 40,
};

function hexToRgb(hex) {
  const clean = hex.replace(/[^0-9a-fA-F]/g, '').slice(0, 6);
  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;
  return rgb(r, g, b);
}

function hexToRgbLight(hex, opacity) {
  const clean = hex.replace(/[^0-9a-fA-F]/g, '').slice(0, 6);
  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;
  // Blend toward white
  return rgb(r * opacity + (1 - opacity), g * opacity + (1 - opacity), b * opacity + (1 - opacity));
}

function fmtCurrency(amount, currencyCode) {
  const decimals = currencyCode === 'JPY' ? 0 : 2;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);
}

function wrapText(text, font, fontSize, maxWidth) {
  if (!text) return [''];
  const words = text.split(/\s+/);
  const lines = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const width = font.widthOfTextAtSize(testLine, fontSize);
    if (width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }
  if (currentLine) lines.push(currentLine);
  return lines.length ? lines : [''];
}

function addressToString(addr) {
  if (Array.isArray(addr)) return addr.filter(Boolean).join('\n');
  return addr || '';
}

export async function generateInvoicePDF(invoice, totals) {
  const pdfDoc = await PDFDocument.create();
  const fontRegular = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  const accentColor = hexToRgb(invoice.accentColor || '#4F46E5');
  const accentLight = hexToRgbLight(invoice.accentColor || '#4F46E5', 0.12);
  const black = rgb(0.1, 0.1, 0.1);
  const darkGray = rgb(0.25, 0.25, 0.25);
  const gray = rgb(0.4, 0.4, 0.4);
  const altRow = rgb(0.97, 0.97, 0.97);
  const white = rgb(1, 1, 1);

  // Embed logo if present
  let logoImage = null;
  if (invoice.companyLogo?.dataUrl) {
    try {
      const base64 = invoice.companyLogo.dataUrl.split(',')[1];
      const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
      if (invoice.companyLogo.dataUrl.includes('image/png')) {
        logoImage = await pdfDoc.embedPng(bytes);
      } else {
        logoImage = await pdfDoc.embedJpg(bytes);
      }
    } catch {
      // Skip logo if embedding fails
    }
  }

  let page = pdfDoc.addPage([PAGE_W, PAGE_H]);
  let y = PAGE_H - MARGIN;

  function newPage() {
    page = pdfDoc.addPage([PAGE_W, PAGE_H]);
    y = PAGE_H - MARGIN;
  }

  function ensureSpace(needed) {
    if (y - needed < MARGIN) {
      newPage();
      return true;
    }
    return false;
  }

  // Draw text, returns the y position after drawing (accounts for wrapped lines)
  function drawText(text, x, yPos, { font = fontRegular, size = 9, color = black, maxWidth } = {}) {
    if (!text) return yPos;
    if (maxWidth) {
      const lines = wrapText(text, font, size, maxWidth);
      for (const line of lines) {
        page.drawText(line, { x: MARGIN + x, y: yPos, size, font, color });
        yPos -= size + 3;
      }
      return yPos;
    }
    page.drawText(String(text), { x: MARGIN + x, y: yPos, size, font, color });
    return yPos - size - 3;
  }

  function drawTextRight(text, xRight, yPos, { font = fontRegular, size = 9, color = black } = {}) {
    const str = String(text);
    const width = font.widthOfTextAtSize(str, size);
    page.drawText(str, { x: MARGIN + xRight - width, y: yPos, size, font, color });
  }

  // Draw multiline text that respects actual newlines in the content
  function drawMultilineText(text, x, yPos, { font = fontRegular, size = 9, color = black, maxWidth = 250 } = {}) {
    if (!text) return yPos;
    const paragraphs = text.split('\n');
    for (const para of paragraphs) {
      if (!para.trim()) {
        yPos -= size + 2;
        continue;
      }
      yPos = drawText(para.trim(), x, yPos, { font, size, color, maxWidth });
    }
    return yPos;
  }

  // ─── Accent bar at top ───
  page.drawRectangle({
    x: 0,
    y: PAGE_H - 6,
    width: PAGE_W,
    height: 6,
    color: accentColor,
  });

  // ─── Header section ───
  y = PAGE_H - MARGIN - 10;

  // Logo — with proper spacing so it doesn't overlap company name
  if (logoImage) {
    // Use stored dimensions or calculate from natural size
    let lw, lh;
    if (invoice.companyLogo?.width && invoice.companyLogo?.height) {
      lw = Math.min(invoice.companyLogo.width * 0.75, 180);
      lh = lw * (invoice.companyLogo.height / invoice.companyLogo.width);
    } else {
      const logoScale = Math.min(150 / logoImage.width, 50 / logoImage.height);
      lw = logoImage.width * logoScale;
      lh = logoImage.height * logoScale;
    }
    page.drawImage(logoImage, { x: MARGIN, y: y - lh, width: lw, height: lh });
    y -= lh + 14; // Extra spacing after logo
  }

  // Company info (left side)
  const headerStartY = y;
  if (invoice.companyName) {
    y = drawText(invoice.companyName, 0, y, { font: fontBold, size: 14, color: black });
    y -= 2;
  }

  // Address — render each line separately
  const companyAddr = addressToString(invoice.companyAddress);
  if (companyAddr) {
    y = drawMultilineText(companyAddr, 0, y, { size: 9, color: darkGray, maxWidth: 250 });
  }

  const companyDetails = [invoice.companyPhone, invoice.companyEmail, invoice.companyWebsite].filter(Boolean);
  for (const line of companyDetails) {
    y = drawText(line, 0, y, { size: 8, color: darkGray });
  }

  // Invoice title + details (right side)
  let rightY = headerStartY + 4;
  drawTextRight('INVOICE', CONTENT_W, rightY, { font: fontBold, size: 22, color: accentColor });
  rightY -= 30;

  const detailLabels = [
    ['Invoice #:', invoice.invoiceNumber],
    ['Date:', invoice.invoiceDate],
    ['Due Date:', invoice.dueDate || 'N/A'],
    ['Terms:', invoice.paymentTerms],
  ];

  for (const [label, value] of detailLabels) {
    drawTextRight(label, CONTENT_W - 100, rightY, { size: 8, color: gray });
    drawTextRight(value || '', CONTENT_W, rightY, { size: 9, font: fontBold, color: black });
    rightY -= 16;
  }

  y = Math.min(y, rightY) - 20;

  // ─── Client section ───
  ensureSpace(80);

  const billToStartY = y;

  // Bill To
  drawText('BILL TO', 0, y, { font: fontBold, size: 8, color: accentColor });
  y -= 14;
  if (invoice.clientName) {
    y = drawText(invoice.clientName, 0, y, { font: fontBold, size: 10, color: black });
    y -= 1;
  }
  if (invoice.clientCompany) {
    y = drawText(invoice.clientCompany, 0, y, { size: 9, color: darkGray });
  }
  const clientAddr = addressToString(invoice.clientAddress);
  if (clientAddr) {
    y = drawMultilineText(clientAddr, 0, y, { size: 9, color: darkGray, maxWidth: 220 });
  }
  if (invoice.clientEmail) {
    y = drawText(invoice.clientEmail, 0, y, { size: 8, color: gray });
  }

  // Ship To (right side, if enabled)
  if (invoice.showShipTo && (invoice.shipToName || invoice.shipToAddress)) {
    let shipY = billToStartY;
    drawText('SHIP TO', CONTENT_W / 2, shipY, { font: fontBold, size: 8, color: accentColor });
    shipY -= 14;
    if (invoice.shipToName) {
      shipY = drawText(invoice.shipToName, CONTENT_W / 2, shipY, { font: fontBold, size: 10, color: black });
      shipY -= 1;
    }
    const shipAddr = addressToString(invoice.shipToAddress);
    if (shipAddr) {
      shipY = drawMultilineText(shipAddr, CONTENT_W / 2, shipY, { size: 9, color: darkGray, maxWidth: 220 });
    }
    y = Math.min(y, shipY);
  }

  y -= 20;

  // ─── Line items table ───
  const ROW_HEIGHT = 24;
  const HEADER_HEIGHT = 26;

  function drawTableHeader() {
    ensureSpace(HEADER_HEIGHT + ROW_HEIGHT);
    page.drawRectangle({
      x: MARGIN,
      y: y - HEADER_HEIGHT + 6,
      width: CONTENT_W,
      height: HEADER_HEIGHT,
      color: accentColor,
    });
    const hY = y - HEADER_HEIGHT + 12;
    drawText('DESCRIPTION', COL.desc + 10, hY, { font: fontBold, size: 7, color: white });
    drawTextRight('QTY', COL.qty + 40, hY, { font: fontBold, size: 7, color: white });
    drawTextRight('UNIT PRICE', COL.price + 60, hY, { font: fontBold, size: 7, color: white });
    drawTextRight('AMOUNT', CONTENT_W - 10, hY, { font: fontBold, size: 7, color: white });
    y -= HEADER_HEIGHT;
  }

  drawTableHeader();

  for (let i = 0; i < invoice.lineItems.length; i++) {
    const item = invoice.lineItems[i];
    const amount = (parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0);

    if (y - ROW_HEIGHT < MARGIN + 180) {
      newPage();
      drawTableHeader();
    }

    // Subtle alternating row background
    if (i % 2 === 1) {
      page.drawRectangle({
        x: MARGIN,
        y: y - ROW_HEIGHT + 6,
        width: CONTENT_W,
        height: ROW_HEIGHT,
        color: altRow,
      });
    }

    const rowY = y - ROW_HEIGHT + 11;
    drawText(item.description || '', COL.desc + 10, rowY, { size: 9, color: black, maxWidth: COL.qty - COL.desc - 20 });
    drawTextRight(String(item.quantity || 0), COL.qty + 40, rowY, { size: 9, color: black });
    drawTextRight(fmtCurrency(parseFloat(item.unitPrice) || 0, invoice.currency), COL.price + 60, rowY, { size: 9, color: black });
    drawTextRight(fmtCurrency(amount, invoice.currency), CONTENT_W - 10, rowY, { size: 9, font: fontBold, color: black });

    y -= ROW_HEIGHT;
  }

  // Bottom border of table
  page.drawLine({
    start: { x: MARGIN, y: y + 6 },
    end: { x: MARGIN + CONTENT_W, y: y + 6 },
    thickness: 0.5,
    color: rgb(0.8, 0.8, 0.8),
  });

  y -= 24;

  // ─── Totals section ───
  ensureSpace(140);

  const totalsX = CONTENT_W - 200;
  const totalsValueX = CONTENT_W - 10;

  function drawTotalLine(label, value, { bold = false, color: lineColor = black, labelColor = gray } = {}) {
    const font = bold ? fontBold : fontRegular;
    const size = bold ? 11 : 9;
    drawText(label, totalsX, y, { font: bold ? fontBold : fontRegular, size: 9, color: labelColor });
    drawTextRight(value, totalsValueX, y, { font, size, color: lineColor });
    y -= 18;
  }

  drawTotalLine('Subtotal', fmtCurrency(totals.subtotal, invoice.currency), { labelColor: darkGray });

  if (invoice.showDiscount && totals.discountAmount > 0) {
    const discLabel = invoice.discountType === 'percentage'
      ? `Discount (${invoice.discountValue}%)`
      : 'Discount';
    drawTotalLine(discLabel, `-${fmtCurrency(totals.discountAmount, invoice.currency)}`, { color: rgb(0.8, 0.15, 0.15), labelColor: darkGray });
  }

  if (invoice.showTax && totals.taxAmount > 0) {
    drawTotalLine(`Tax (${invoice.taxRate}%)`, fmtCurrency(totals.taxAmount, invoice.currency), { labelColor: darkGray });
  }

  if (invoice.showShipping && totals.shipping > 0) {
    const shippingLabel = invoice.shippingLabel || 'Shipping';
    drawTotalLine(shippingLabel, fmtCurrency(totals.shipping, invoice.currency), { labelColor: darkGray });
  }

  // Total line with separator
  page.drawLine({
    start: { x: MARGIN + totalsX, y: y + 12 },
    end: { x: MARGIN + totalsValueX, y: y + 12 },
    thickness: 1.5,
    color: black,
  });
  y -= 2;
  drawTotalLine('Total', fmtCurrency(totals.total, invoice.currency), { bold: true, labelColor: black });

  if (totals.amountPaid > 0) {
    drawTotalLine('Amount Paid', fmtCurrency(totals.amountPaid, invoice.currency), { labelColor: darkGray });
  }

  // Balance Due — light accent background, readable text
  y -= 2;
  const balanceBoxH = 28;
  page.drawRectangle({
    x: MARGIN + totalsX - 10,
    y: y - 8,
    width: CONTENT_W - totalsX + 20,
    height: balanceBoxH,
    color: accentLight,
  });
  drawText('Balance Due', totalsX, y + 2, { font: fontBold, size: 10, color: black });
  drawTextRight(fmtCurrency(totals.balanceDue, invoice.currency), totalsValueX, y + 2, { font: fontBold, size: 12, color: accentColor });
  y -= balanceBoxH + 10;

  // ─── Footer section ───
  function drawFooterSection(label, text) {
    if (!text) return;
    ensureSpace(40);
    drawText(label, 0, y, { font: fontBold, size: 7, color: accentColor });
    y -= 12;
    y = drawMultilineText(text, 0, y, { size: 8, color: darkGray, maxWidth: CONTENT_W });
    y -= 6;
  }

  if (invoice.notes || invoice.paymentInstructions || invoice.termsAndConditions) {
    y -= 4;
    page.drawLine({
      start: { x: MARGIN, y: y + 14 },
      end: { x: MARGIN + CONTENT_W, y: y + 14 },
      thickness: 0.5,
      color: rgb(0.85, 0.85, 0.85),
    });
    y -= 4;
  }

  drawFooterSection('NOTES', invoice.notes);
  drawFooterSection('PAYMENT INSTRUCTIONS', invoice.paymentInstructions);
  drawFooterSection('TERMS & CONDITIONS', invoice.termsAndConditions);

  return pdfDoc.save();
}
